import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { isLovablePreview } from "./utils/urlUtils";
import { initContrastAudit } from "./utils/contrastAudit";

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

// Global error handlers to surface runtime issues
window.addEventListener('error', (e) => {
  const msg = e.error?.stack || e.message || 'Unknown error';
  console.error('[TinyMinds GlobalError]', msg);
  
  // Only show error banner in preview for debugging
  if (isLovablePreview() && process.env.NODE_ENV === 'development') {
    const id = 'global-error-banner';
    if (!document.getElementById(id)) {
      const el = document.createElement('div');
      el.id = id;
      el.style.cssText = 'position:fixed;bottom:8px;left:8px;right:8px;z-index:99999;background:#fee;border:1px solid #f88;color:#900;padding:8px;font:12px/1.4 system-ui;white-space:pre-wrap;';
      el.textContent = `TinyMinds runtime error: ${msg}`;
      document.body.appendChild(el);
    }
  }
});

window.addEventListener('unhandledrejection', async (e) => {
  const msg = (e.reason && (e.reason.stack || e.reason.message)) || String(e.reason);
  console.error('[TinyMinds UnhandledRejection]', msg);
  
  // Only handle chunk errors in production
  const isChunkError = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk/i.test(msg);
  if (isChunkError && !isLovablePreview()) {
    const FLAG = 'tm_chunk_error_handled';
    if (!sessionStorage.getItem(FLAG)) {
      sessionStorage.setItem(FLAG, '1');
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
        if (window.caches) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
      } catch (err) {
        console.warn('Recovery after chunk error failed:', err);
      } finally {
        window.location.reload();
      }
    }
  }
});

console.log('[TinyMinds Bootstrap] Starting app at', window.location.href);
console.log('[TinyMinds Bootstrap] Is Lovable preview?', isLovablePreview());

// Render the app immediately with React 18 features
const root = createRoot(container);
root.render(<App />);

// Dev-only: surface low-contrast text in console + floating badge
initContrastAudit();

// Setup notifications and service workers only for production
if (!isLovablePreview()) {
  // Use requestIdleCallback for non-critical initialization
  const initializeNonCriticalFeatures = () => {
    // Note: Notification permissions should only be requested via user interaction
    // requestNotificationPermission() can be called from user-initiated actions
    
    // Register service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      }).then(
        (registration) => {
          console.log('ServiceWorker registration successful with scope: ', registration.scope);
        },
        (err) => {
          console.log('ServiceWorker registration failed: ', err);
        }
      );
      
      // Firebase Messaging service worker registration removed to prevent
      // automatic notification permission prompts on page load
      // Can be re-enabled when notification feature is implemented with user consent
      // navigator.serviceWorker.register('/firebase-messaging-sw.js').then(
      //   (registration) => {
      //     console.log('Firebase Service Worker registered with scope:', registration.scope);
      //   },
      //   (err) => {
      //     console.error('Firebase Service Worker registration failed:', err);
      //   }
      // );
    }
  };

  // Use requestIdleCallback if available, otherwise fallback to setTimeout
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initializeNonCriticalFeatures, { timeout: 2000 });
  } else {
    setTimeout(initializeNonCriticalFeatures, 100);
  }
} else {
  console.log('[TinyMinds Preview] Skipping service worker registration for Lovable preview');
}